import { DISPLAYED_CALENDAR_TABLE_VIEW } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/displayedCalendarTableView';
import { DateTableColumnsProvider } from './_table/fragment/DateTableColumns';
import { usePricingRights } from '@util/PricingRightsHook';
import SiteNotificationFragment from '@app/site/secured/_common/layout/fragment/SiteNotificationFragment';
import RefreshMarketStateToolbar
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/RefreshMarketStateToolbar';
import React from 'react';
import MarketStateToolsProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/MarketStateToolsProvider';
import DashboardHeaderFiltersFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/filters/DashboardHeaderFiltersFragment';
import ManageCompetitorColumnsOrderComponent
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/ManageCompetitorColumnsOrderComponent';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import {
	useMarketStateFilterInputTools
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import PriceRecommendationLiteGlobalNotification
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/fragment/PriceRecommendationLiteGlobalNotification';
import PricingWorkflowActions
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/PricingWorkflowActions';
import FullWidthTableButton
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/FullWidthTableButton';
import { useFullWidthTable } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/useFullWidthTable';
import { CalendarTableSettingsView } from '@app/model/company_hotel/settings/CalendarTableSettingsView';
import Utilities from '@util/Utilities';


const MarketStatePage: React.FunctionComponent<{ setIsLoading: (loading: boolean) => void }> = () => {

	const { companyHotel } = useSelectedCompanyHotel();

	return <MarketStateToolsProvider companyHotel={companyHotel}>
		<DateTableColumnsProvider><MarketStateInternal companyHotel={companyHotel} /></DateTableColumnsProvider>
	</MarketStateToolsProvider>;
};

const MarketStateInternal: React.FunctionComponent<{
	companyHotel: CompanyHotel
}>
	= ({ companyHotel }) => {
	const { canViewRecommendations, canChangePrices } = usePricingRights(companyHotel);

	const tools = useMarketStateTools();
	const { filter } = useMarketStateFilterInputTools();

	const location = useLocation();
	const isTable = !!matchPath('/secured/rateshopper/rates/market/table', location.pathname);
	const isCalendar = !!matchPath('/secured/rateshopper/rates/market/calendar', location.pathname);
	const isGraph = !!matchPath('/secured/rateshopper/rates/market/graph', location.pathname);
	// The displayed layout is independent of the hotel's saved table preference.
	const isDateBasedTable = isTable && DISPLAYED_CALENDAR_TABLE_VIEW === CalendarTableSettingsView.CLASSIC;
	const [isFullWidth, toggleFullWidth] = useFullWidthTable();
	const isWide = isDateBasedTable && isFullWidth;

	const canSubmitPrices = canChangePrices && companyHotel.priceRecommendationSettings?.canSubmitPrices === true && companyHotel.pricingIntegrationActive === true;
	const canShowManualPriceRecommendationTrigger =
		tools.companyHotel.priceRecommendationSettings?.showManualPriceRecommendationTrigger === true;

	return <>
		<DashboardHeaderFiltersFragment />
		<main>
			<div className={Utilities.classNames('mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8', isWide && 'xl:max-w-none xl:px-4')}>
				<SiteNotificationFragment />
				{isCalendar && (canViewRecommendations && companyHotel.hasRecommendationEngineRoompulseLite()) && <PriceRecommendationLiteGlobalNotification />}
				<RefreshMarketStateToolbar companyHotel={companyHotel} calendarMonth={tools.calendarMonth}
					afterUpdateMarketStateButton={isTable && <>
						{isDateBasedTable && <FullWidthTableButton isFullWidth={isFullWidth} onToggle={toggleFullWidth} />}
						<ManageCompetitorColumnsOrderComponent companyHotel={companyHotel} calendarMonth={tools.calendarMonth} />
					</>}>
					{(isCalendar || isTable) && canChangePrices && tools.companyHotel.hasPriceRecommendationModule() && canShowManualPriceRecommendationTrigger
						? <PricingWorkflowActions
							companyHotel={tools.companyHotel}
							calendarMonth={tools.calendarMonth}
							filter={filter}
							canSubmitMonthlyPrices={canSubmitPrices} />
						: <div />}
					{isGraph &&  <div/>}
				</RefreshMarketStateToolbar>
				<Outlet />
			</div>
		</main>
	</>;
};

export default MarketStatePage;
